/**
 * tracks.json (backend: src/app/services/sam3/video_tracks.py) → an index the overlay can paint from at video
 * frame rate, and the counts the side panel shows. All pure functions; nothing here touches the DOM.
 *
 * tracks.json: { fps, width, height, n_frames, time_offset_sec, counting_method, confidence_threshold,
 *                tracks: [{ id, t0, confirmed_at, rel_size, conf, t: [], x: [], y: [], w: [], h: [] }] }
 * conf (a track's highest detection confidence) and confidence_threshold (the job's) are absent in older files.
 * Frame t of a track plays at t / fps in clean_video (the analysed clip).
 */

/** The lowest sensitivity the slider offers: the job's threshold (nothing below it was tracked), else 0. */
export const minSensitivity = doc => Number(doc?.confidence_threshold) || 0;

/** Whether the file can be filtered by confidence at all (older tracks.json has no per-track conf). */
export const hasConfidence = doc =>
	!!doc?.tracks?.length && doc.tracks.every(t => Number.isFinite(t.conf));

/** The same document with only the tracks whose highest confidence reaches `min`. */
export const filterByConfidence = (doc, min) => {
	if (!doc || !hasConfidence(doc) || !(min > minSensitivity(doc))) return doc;
	return { ...doc, tracks: doc.tracks.filter(t => t.conf >= min) };
};

/** Body length below this share of the herd's typical animal at the same distance counts as calf-sized. */
export const CALF_RATIO = 0.75;

/** Build the per-frame index: which track points are visible in each frame, plus counts over time. */
export const indexTracks = doc => {
	const n = Math.max(1, doc.n_frames | 0);
	const tracks = doc.tracks || [];
	// counting sort of (track, point) pairs by frame
	const perFrame = new Int32Array(n + 1);
	for (const tr of tracks) for (const t of tr.t) if (t >= 0 && t < n) perFrame[t + 1] += 1;
	for (let i = 0; i < n; i += 1) perFrame[i + 1] += perFrame[i];
	const fill = perFrame.slice(0, n);
	const total = perFrame[n];
	const trackOf = new Int32Array(total);
	const pointOf = new Int32Array(total);
	tracks.forEach((tr, k) => {
		tr.t.forEach((t, j) => {
			if (t < 0 || t >= n) return;
			const at = fill[t];
			trackOf[at] = k;
			pointOf[at] = j;
			fill[t] += 1;
		});
	});
	// unique (confirmed) count over time and animals in view per frame
	const confirmedPerFrame = new Int32Array(n);
	for (const tr of tracks) {
		const c = Math.min(n - 1, Math.max(0, tr.confirmed_at ?? tr.t0 ?? 0));
		confirmedPerFrame[c] += 1;
	}
	const uniqueSeries = new Int32Array(n);
	let run = 0;
	for (let i = 0; i < n; i += 1) {
		run += confirmedPerFrame[i];
		uniqueSeries[i] = run;
	}
	const inView = new Int32Array(n);
	for (let i = 0; i < n; i += 1) inView[i] = perFrame[i + 1] - perFrame[i];
	let maxInView = 0;
	let maxInViewFrame = 0;
	for (let i = 0; i < n; i += 1) {
		if (inView[i] > maxInView) {
			maxInView = inView[i];
			maxInViewFrame = i;
		}
	}
	return {
		doc,
		fps: doc.fps || 30,
		width: doc.width,
		height: doc.height,
		nFrames: n,
		tracks,
		perFrame,
		trackOf,
		pointOf,
		uniqueSeries,
		inView,
		maxInView,
		maxInViewFrame,
	};
};

const cross = (a, b, p) => (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);

/**
 * +1 / −1 when the move p→q crosses the drawn segment a→b towards its positive / negative side, else 0. A point on
 * the line belongs to the positive side. Same rule as the backend (LineCrossingModule.side_change).
 */
const sideChange = (a, b, p, q) => {
	const before = cross(a, b, p) >= 0;
	const after = cross(a, b, q) >= 0;
	if (before === after) return 0;
	if (cross(p, q, a) * cross(p, q, b) > 0) return 0;
	return after ? 1 : -1;
};

/** Frames within which a track's repeated crossing of the same line is one event (backend: 5 source frames). */
const DEBOUNCE_FRAMES = 5;

/**
 * Crossings of one line along every track's trajectory, as the backend counts them: the net count is the signed sum
 * of every side change (|one way − back|, no debounce, so jitter on the line cancels); crossing events are side
 * changes with a 5-frame debounce per track. Returns { net, events, netSeries: Int32Array(nFrames) }.
 */
export const lineCrossings = (index, line) => {
	const { tracks, nFrames } = index;
	const a = line.start;
	const b = line.end;
	const changes = [];
	let events = 0;
	for (const tr of tracks) {
		let last = -Infinity;
		for (let j = 1; j < tr.t.length; j += 1) {
			const d = sideChange(a, b, [tr.x[j - 1], tr.y[j - 1]], [tr.x[j], tr.y[j]]);
			if (!d) continue;
			changes.push([tr.t[j], d]);
			if (tr.t[j] - last >= DEBOUNCE_FRAMES) events += 1;
			last = tr.t[j];
		}
	}
	changes.sort((c1, c2) => c1[0] - c2[0]);
	const netSeries = new Int32Array(nFrames);
	let sum = 0;
	let k = 0;
	for (let i = 0; i < nFrames; i += 1) {
		while (k < changes.length && changes[k][0] <= i) {
			sum += changes[k][1];
			k += 1;
		}
		netSeries[i] = Math.abs(sum);
	}
	return { net: Math.abs(changes.reduce((acc, c) => acc + c[1], 0)), events, netSeries };
};

/** Relative body sizes of the animals counted up to frame `frame` (for the histogram and the calf-sized share). */
export const sizesUpTo = (index, frame) => {
	const out = [];
	for (const tr of index.tracks) {
		if ((tr.confirmed_at ?? tr.t0) <= frame && Number.isFinite(tr.rel_size))
			out.push(tr.rel_size);
	}
	return out;
};

/** Histogram of relative sizes on fixed bins (0.35–1.75), as in the counting video. */
export const sizeHistogram = (sizes, bins = 22, lo = 0.35, hi = 1.75) => {
	const counts = new Array(bins).fill(0);
	const width = (hi - lo) / bins;
	for (const v of sizes) {
		const i = Math.min(bins - 1, Math.max(0, Math.floor((v - lo) / width)));
		counts[i] += 1;
	}
	return { counts, lo, hi, width, calf: sizes.filter(v => v < CALF_RATIO).length };
};

/** Video time (s) → frame index of the analysed clip. */
export const frameAt = (index, seconds) =>
	Math.min(index.nFrames - 1, Math.max(0, Math.round(seconds * index.fps)));

/** A line in the backend's virtual_line format ({start_point, end_point}) → { start, end }, or a default across the middle. */
export const lineFromAnalysis = (virtualLine, index) => {
	if (virtualLine?.start_point && virtualLine?.end_point) {
		return { start: [...virtualLine.start_point], end: [...virtualLine.end_point] };
	}
	const y = Math.round(index.height / 2);
	return { start: [0, y], end: [index.width, y] };
};
